import { unwrapResult } from "@reduxjs/toolkit";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import forwardarrow from "../../../../assets/images/arrow_forwardb.svg";
import Button from "../../../../components/common/Button/Button";
import SummaryContent from "../../../../components/common/SummaryContent/SummaryContent";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import AddMoreCoversMotorLayout from "../../../../pages/motorInsurancePages/addMOreCoversFlow/addMoreCoversMotorLayout";
import { PolicyViewResponse } from "../../../../redux/features/motor/getQuoteSlice";
import { selectNewMotorForm } from "../../../../redux/features/newMotorFormSlice";
import {
  getPolicyPayLoading,
  policyPayAddCovers,
} from "../../../../redux/features/payment/policyPaySlice";
import { getClientSource } from "../../../../utils/getClientSource";
import { applyPromoCodeToQuote, calculatePromoDiscount } from "../../../../utils/promoCodeUtils";

export const ADD_COVERS_ENHANCEMENT_TRAVEL = "ADD_COVERS_ENHANCEMENT_TRAVEL";

const ReviewAndConfirmTravelPage = ({ handleNavigationToPolicyOverview }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const translate = useLanguageTranslate();
  const [formState, setFormState] = useState({});
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoCodeApplied, setPromoCodeApplied] = useState(false);
  const [promoCodeDiscount, setPromoCodeDiscount] = useState(0);
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  const [promoAppliedQuoteData, setPromoAppliedQuoteData] = useState(null);
  const { policy_id } = useParams();

  const policyPayLoading = useSelector(getPolicyPayLoading);
  const policyInfo = useSelector(PolicyViewResponse);

  // Get selected covers from Redux (from enhance coverage step)
  const finalCovers = useSelector(selectNewMotorForm)?.[
    ADD_COVERS_ENHANCEMENT_TRAVEL
  ] || { coverOptionsState: [] };

  console.log("📋 Review page - covers from Redux:", finalCovers);

  // Filter to include only items where `included` is true
  const includedCoversAll =
    finalCovers?.coverOptionsState?.filter(
      (cover) =>
        (cover.included == true) & (cover.defaultIncludedInPlan == false)
    ) ?? [];
  console.log("Included Covers: ", includedCoversAll);

  const includedCovers =
    finalCovers?.coverOptionsState
      ?.filter(
        (cover) =>
          cover.included === true && cover.defaultIncludedInPlan === false
      )
      ?.map((cover) => cover.coverCode || cover.code) ?? [];
  const includedCoversString = includedCovers.join(",");

  const additionalPremium = finalCovers?.coverOptionsState
    .filter(
      (option) =>
        option.included &&
        Number(option.price) > 0 &&
        !option.defaultIncludedInPlan
    ) // Ensure price is a number
    .reduce((sum, option) => sum + Number(option.price), 0);

  const totalPrice = additionalPremium + additionalPremium * 0.1;

  // Calculate VAT as 10% of the total price
  const VAT = additionalPremium * 0.1;

  console.log(
    "on travel add covers payment page, final covers are: ",
    finalCovers
  );
  console.log("on travel add covers payment page, policy info: ", policyInfo);

  // Build planData for SummaryContent component
  const planData = {
    basePremium: additionalPremium,
    premium: additionalPremium,
    tax: VAT,
    totalPremium: totalPrice,
    quoteNumber: policyInfo?.PolicyNumber || "N/A",
    policyNumber: policyInfo?.PolicyNumber || "N/A",
    // Build covers array for add-on display matching SummaryContent format
    Covers: includedCoversAll.map((cover) => {
      const code = cover.coverCode || cover.code;
      return {
        coverCode: code,
        coverName: code === "CPER17" ? "Winter Activities" : code === "CPER137" ? "Summer Activities" : code === "CPER77" ? "Terrorism Extension" : cover.name,
        Price: Number(cover.price) || 0,
      };
    }),
    // Provide getCoverPriceByCode function for SummaryContent
    getCoverPriceByCode: (coverCode) => {
      const cover = includedCoversAll.find((c) => (c.coverCode || c.code) === coverCode);
      return cover ? Number(cover.price) || 0 : 0;
    },
  };

  const applyPromoCode = async () => {
    const quoteNum = policyInfo?.PolicyNumber;
    setIsApplyingPromo(true);
    const result = await applyPromoCodeToQuote(quoteNum, promoCode, translate);
    if (result.success) {
      setPromoAppliedQuoteData(result.data);
      setPromoCodeApplied(true);
      setPromoCodeDiscount(calculatePromoDiscount(totalPrice, result.data.TotalPremium));
    } else {
      toast.error(result.error);
    }
    setIsApplyingPromo(false);
  };

  const clearPromoCode = () => {
    setPromoCode("");
    setPromoCodeApplied(false);
    setPromoCodeDiscount(0);
    setPromoAppliedQuoteData(null);
  };

  const handlePayment = async () => {
    const finalQuoteNumber = promoCodeApplied && promoAppliedQuoteData ? promoAppliedQuoteData.PolicyNumber : (policyInfo?.PolicyNumber ?? "");
    const finalTotal = promoCodeApplied && promoAppliedQuoteData ? promoAppliedQuoteData.TotalPremium : totalPrice;
    const payload = {
      PolicyNumber: finalQuoteNumber,
      Premium: finalTotal ?? "",
      Channel: getClientSource(),
      Covers: includedCoversString ?? "",
      uatAmount: `${totalPrice}` ?? "",
    };

    try {
      console.log("Final selected travel covers are: ", includedCovers);
      // Calling API policy/pay
      const linkAction = await dispatch(policyPayAddCovers(payload));
      const link = unwrapResult(linkAction);
      // console.log("Link is: ", link)
      if (link) {
        window.open(link, "_self");
      }
    } catch (error) {
      // alert("Error in getting payment link: ", error)
      toast.error("Error in getting payment link: ", error);
    }
  };

  return (
    <AddMoreCoversMotorLayout
      is_bottom_sticky_layout={false}
      useTravelFlow={true}
      handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
    >
      <div className="flex flex-col items-center mx-auto max-w-lg max767:p-4 pt-[1%]">
        <div className="flex justify-center items-center w-full">
          <div className=" p-0 w-full ">
            <TitleDescription
              title={translate("Review and Confirm", "المراجعة والتأكيد")}
              description={translate(
                "Please review your information and proceed to payment.",
                "يرجى مراجعة التفاصيل أدناه وتأكيد الدفع."
              )}
            />
            <div className="bg-[#F6F8F9] rounded-[26px] p-4 max767:bg-white max767:p-0 mb-4 rtl:max767:border-t max767:rounded-none max767:!pb-4">
              <h3 className="text-[#1A2024] text-base not-italic font-semibold rtl:max767:font-medium leading-6 tracking-[-0.16px] rtl:max767:pt-4 rtl:mb-4 ">
                {translate("Added Covers", "أنت تقوم بإضافة أغطية إضافية إلى سياستك")}
              </h3>
              <p className="text-[#1A2024] text-sm mb-2 rtl:!hidden">
                {translate(
                  "Review the details below and confirm the payment",
                  "راجع التفاصيل أدناه وأكد الدفع"
                )}
              </p>

              <div className="max767:overflow-x-scroll">
                <table className="min-w-full border-separate bg-white border border-solid border-[#DDE2E4] border-spacing-0 rounded-[10px]">
                  <tbody>
                    {/* Passengers rows - FIRST at the top */}
                    {/* Passenger 1 is always the policy holder (Party) — full info. */}
                    <tr className="border-b">
                      <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm not-italic font-medium leading-6 h-[44px]">
                        {translate("Passenger 1", "المسافر ١")}
                      </td>
                      <td className="py-2 px-4 border-b text-[#1A2024] text-sm not-italic font-medium leading-6">
                        {`${policyInfo?.PolicyHeader?.Party?.FirstName || ""} ${
                          policyInfo?.PolicyHeader?.Party?.LastName || ""
                        }`.trim() || "N/A"}
                      </td>
                    </tr>
                    {/* Family plans: additional members from the Passengers list. */}
                    {/* Skip empty/placeholder entries (no name) so individual
                        plans don't show a stray "Passenger 2 — N/A". */}
                    {(policyInfo?.PolicyHeader?.Passengers || [])
                      .map(
                        (passenger) =>
                          passenger.FullName ||
                          passenger.fullName ||
                          `${passenger.FirstName || ""} ${
                            passenger.LastName || ""
                          }`.trim()
                      )
                      .filter(Boolean)
                      .map((name, index) => (
                        <tr key={`passenger-${index}`} className="border-b">
                          <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm not-italic font-medium leading-6 h-[44px]">
                            {translate(
                              `Passenger ${index + 2}`,
                              `المسافر ${index + 2}`
                            )}
                          </td>
                          <td className="py-2 px-4 border-b text-[#1A2024] text-sm not-italic font-medium leading-6">
                            {name}
                          </td>
                        </tr>
                      ))}
                    <tr className="border-b">
                      <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm not-italic font-medium leading-6 h-[44px]">
                        {translate("Departure date", "تاريخ المغادرة")}
                      </td>
                      <td className="py-2 px-4 border-b text-[#1A2024] text-sm not-italic font-medium leading-6">
                        {policyInfo?.PolicyHeader?.FromDateStr}
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm not-italic font-medium leading-6 h-[44px]">
                        {translate("Return date", "تاريخ العودة")}
                      </td>
                      <td className="py-2 px-4 border-b text-[#1A2024] text-sm not-italic font-medium leading-6">
                        {policyInfo?.PolicyHeader?.ToDateStr}
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm not-italic font-medium leading-6 h-[44px]">
                        {translate("Plan", "الخطة")}
                      </td>
                      <td className="py-2 px-4 border-b text-[#1A2024] text-sm not-italic font-medium leading-6">
                        {policyInfo?.PolicyHeader?.PlanCode === "GO"
                          ? translate("Gold", "ذهبية")
                          : policyInfo?.PolicyHeader?.PlanCode === "PL"
                          ? translate("Platinum", "بلاتينية")
                          : policyInfo?.PolicyHeader?.PlanCode === "SILVER"
                          ? translate("Silver", "فضية")
                          : policyInfo?.PolicyHeader?.PlanName}
                      </td>
                    </tr>
                    {/* Add-ons row - show additional covers in table */}
                    {includedCoversAll &&
                      includedCoversAll.length > 0 &&
                      includedCoversAll.map((cover, index) => (
                        <tr key={`addon-${index}`} className="border-b">
                          <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm not-italic font-medium leading-6 h-[44px]">
                            {index === 0 ? translate("Add-ons", "الإضافات") : ""}
                          </td>
                          <td className="py-2 px-4 border-b text-[#1A2024] text-sm not-italic font-medium leading-6">
                            {translate(cover.name, cover.name_ar)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-[#F6F8F9] rounded-[26px] p-4 max767:bg-white max767:p-0 mb-4 w-full summary-box">
          <SummaryContent
            planData={promoCodeApplied && promoAppliedQuoteData ? { ...planData, premium: promoAppliedQuoteData.Premium ?? planData.premium, tax: promoAppliedQuoteData.Tax ?? planData.tax, totalPremium: promoAppliedQuoteData.TotalPremium ?? planData.totalPremium, quoteNumber: promoAppliedQuoteData.PolicyNumber ?? planData.quoteNumber } : planData}
            promoCode={promoCode}
            setPromoCode={setPromoCode}
            applyPromoCode={applyPromoCode}
            clearPromoCode={clearPromoCode}
            promoCodeApplied={promoCodeApplied}
            promoCodeDiscount={promoCodeDiscount}
            isApplyingPromo={isApplyingPromo}
            showQuotationNumber={true}
            showPromoCode={true}
            className="!bg-transparent"
            flowType="travelAddCovers"
          />
        </div>

        {/* Policy Notice */}
        <div className="mb-6 p-4 bg-[#FDE9D2] border border-[#FBC98E] rounded-lg flex items-start space-x-3 rtl:space-x-reverse w-full">
          <div className="flex-shrink-0 mt-0.5">
            <svg
              className="w-5 h-5 text-[#A56213]"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div>
            <p className="text-[#252C32] font-semibold text-sm">
              {translate(
                "Kindly note that the policy will be issued under your name",
                "يرجى ملاحظة أن الوثيقة ستصدر باسمك"
              )}
            </p>
          </div>
        </div>

        {/* Terms and Conditions */}
        <div className="mb-6 w-full  max767:!pb-20">
          <label className="flex items-start space-x-3 rtl:space-x-reverse cursor-pointer">
            <label className="flex items-center cursor-pointer gap-2">
              <input
                type="checkbox"
                className="peer hidden"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
              />
              <div
                className="size-5 border-2 border-gray-400 rounded-[4px]
                  peer-checked:bg-[#00A9E7] peer-checked:border-[#00A9E7]
                  flex items-center justify-center transition-all"
              >
                <svg
                  width="12"
                  height="9"
                  viewBox="0 0 12 9"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M11 0C10.72 0 10.47 0.11 10.29 0.29L4 6.59L1.71 4.29C1.53 4.11 1.28 4 1 4C0.45 4 0 4.45 0 5C0 5.28 0.11 5.53 0.29 5.71L3.29 8.71C3.47 8.89 3.72 9 4 9C4.28 9 4.53 8.89 4.71 8.71L11.71 1.71C11.89 1.53 12 1.28 12 1C12 0.45 11.55 0 11 0Z"
                    fill="white"
                  />
                </svg>
              </div>
            </label>
            <div className="text-sm text-[#252C32] rtl:font-medium leading-[24px] text-justify">
              <span>
                {translate(
                  "I hereby certify that the above given information is true and correct to the best of my knowledge, and I agree to cooperate fully by providing all requested documents before payment. I understand that providing invalid information will result in my insurance policy to be void. I also read and agree the",
                  "أشهد بموجب هذا أن المعلومات المقدمة أعلاه صحيحة ودقيقة حسب أفضل ما أعلم، وأوافق على التعاون الكامل من خلال تقديم جميع الوثائق المطلوبة قبل الدفع. أفهم أن تقديم معلومات غير صحيحة سيؤدي إلى إبطال وثيقة التأمين الخاصة بي. كما أنني قرأت وأوافق على"
                )}
              </span>{" "}
              <button
                onClick={() =>
                  window.open(
                    "https://content.solidarity.bh/uploads/TRAVEL_INSURANCE_WORDING_30_08_2023_8e74afd369_44e9596879.pdf",
                    "_blank"
                  )
                }
                className="text-[#00384D] underline hover:text-[#0088CC] font-bold"
              >
                {translate("Policy Specification", "مواصفات وثيقة")}
              </button>{" "}
              <span>
                {translate("of this Insurance product.", "التأمين هذه.")}
              </span>
            </div>
          </label>
        </div>

        {/* Proceed Button - Outside of gray box */}
        <div className="max767:footer max767:bg-white max767:border-t-[#E5E9EB] max767:border-t max767:border-solid max767:mt-2 max767:w-full max767:py-2 flex max767:flex-col !gap-4 mt-4 max767:fixed max767:bottom-0 max767:w-full max767:p-4 max767:inset-x-0 ipad:items-center max767:inline-block max767:z-[9] w-full">
          <Button
            isLoading={policyPayLoading}
            disabled={!agreedToTerms}
            className="bg-[#55C6EF] mt-4 mb-9 max767:mb-4 w-full text-[#48535B] text-lg not-italic font-semibold leading-6 tracking-[-0.18px] flex justify-center p-3 rounded-[10px] cursor-pointer w-full"
            label={translate("Proceed to Payment", "انتقل إلى الدفع")}
            onSubmit={handlePayment}
            post_icon={forwardarrow}
            post_icon_class={"w-6 h-6 ml-[5px] rtl:rotate-180 rtl:mr-2"}
            fontSize="text-lg max767:text-sm"
          />
        </div>
      </div>
    </AddMoreCoversMotorLayout>
  );
};

export default ReviewAndConfirmTravelPage;
